import { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "@/lib/config";
import { useAuthStep } from "@/hooks/useAuthStep";
import { Check, Loader2, RotateCcw } from "lucide-react";
import { useCommunication } from "@/lib/CommunicationWrapper";

const supabase = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);

const MagicLink = () => {

    const { emailLogin, setAuthStep, setToken, handleAuth, loginState } = useAuthStep();
    const { updateOptions } = useCommunication();

    const [loading, setLoading] = useState<boolean>(false);
    const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
    const [success, setSuccess] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = useState<number>(60);
    const [resendLoading, setResendLoading] = useState<boolean>(false);

    const handleCodeChange = (index: number, value: string) => {
        if (value.length > 1) return; // Only allow single digit

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.querySelector(`input[data-index="${index + 1}"]`) as HTMLInputElement;
            if (nextInput) nextInput.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            // Move to previous input on backspace if current is empty
            const prevInput = document.querySelector(`input[data-index="${index - 1}"]`) as HTMLInputElement;
            if (prevInput) prevInput.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        const digits = pastedData.replace(/\D/g, '').slice(0, 6); // Only keep digits, max 6

        if (digits.length === 6) {
            const newCode = [...code];
            for (let i = 0; i < 6; i++) {
                newCode[i] = digits[i] || '';
            }
            setCode(newCode);

            // Focus the last input or submit if complete
            if (digits.length === 6) {
                const lastInput = document.querySelector(`input[data-index="5"]`) as HTMLInputElement;
                if (lastInput) lastInput.focus();
            }
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;

        setResendLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.signInWithOtp({
                email: emailLogin,
            });

            if (error) {
                setError("Failed to resend code. Please try again.");
                console.error(error);
            } else {
                // Start 60 second cooldown
                setResendCooldown(60);
            }
        } catch (err) {
            setError("Failed to resend code. Please try again.");
            console.error(err);
        } finally {
            setResendLoading(false);
        }
    };

    useEffect(() => {
        const fullCode = code.join('');
        if (fullCode.length === 6) {
            handleSubmit();
        }
    }, [code]);

    useEffect(() => {
        if (success !== true) return;

    }, []);

    // Handle resend cooldown countdown
    useEffect(() => {
        if (resendCooldown <= 0) return;

        const timer = setTimeout(() => {
            setResendCooldown(prev => prev - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const handleSubmit = async () => {
        setError(null);
        setLoading(true);
        const fullCode = code.join('');
        if (fullCode.length !== 6) return;
        const authFn = supabase.auth.verifyOtp({
            email: emailLogin,
            token: fullCode,
            type: 'email', // The type must be 'email'
        });
        handleAuth(authFn, "signup");

        return;

        setToken(data);

        updateOptions({
            "auth": {
                "access_token": data?.session?.access_token,
                "refresh_token": data?.session?.refresh_token,
                "expires_at": data?.session?.expires_at,
                "user": data?.session?.user,
            },
            "auth_state": "complete",
            "should_show_spotlight": true,
        });

        window?.top?.postMessage({
            type: "AUTH_SUCCESS",
        }, "*");

        if (error) {
            setError("There was an error verifying your code. Please try again.");
            setLoading(false);
            console.error(error);
        } else {
            setLoading(false);
            setSuccess(true);
            setToken(data);
            localStorage.setItem("last_used_login_method", "email");
            setTimeout(() => {
                setAuthStep("questionairre");
            }, 2000);
        }

    };

    const getProgressPercentage = () => {
        return ((60 - resendCooldown) / 60) * 100;
    };

    return (
        <div className="px-[30px] text-gray-500">
            <h1 className="text-2xl font-semibold text-black mb-[5px]">👉 Check your inbox</h1>
            <p className="mb-6">We sent a code to {emailLogin} - pop it in below & you're in</p>

            <div className="flex gap-2 justify-center mb-6">
                {code.map((digit, index) => (
                    <input
                        key={index}
                        data-index={index}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                        placeholder="0"
                    />
                ))}
            </div>

            <div className="mb-10">
                {loading && <p className="text-gray-500 text-sm flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</p>}
                {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                {success && <p className="text-gray-500 text-sm flex items-center gap-2 justify-center"><Check className="w-4 h-4" /> Success!</p>}
            </div>

            {/* Resend button with progress wheel */}
            <div className="flex flex-col items-center justify-center">
                <p className="text-xs text-gray-500 mb-2">If the email doesn't show up within 5 minutes, click below to resend.</p>
                <button
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || resendLoading}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${resendCooldown > 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-[#ff895f] text-white hover:bg-[#ff895f]/80'
                        }`}
                >
                    <p className="z-1 relative flex items-center gap-2">
                        {resendLoading ? (<Loader2 className="w-4 h-4 animate-spin" />) : (<RotateCcw className="w-4 h-4" />)}
                        <span>{resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}</span>
                    </p>


                    {/* Progress wheel */}
                    {resendCooldown > 0 && (
                        <div className="absolute inset-0 rounded-lg overflow-hidden">
                            <div
                                className="h-full bg-[#ff895f]/20 transition-all duration-1000 ease-linear"
                                style={{ width: `${getProgressPercentage()}%` }}
                            />
                        </div>
                    )}
                </button>
            </div>
        </div>
    )
}

export default MagicLink;